import React, { FC } from "react";
import { JobPostEditorSectionLayout } from "@components/Layout/JobPostEditorSectionLayout";
import { useFormContext } from "react-hook-form";
import { Input } from "@components/Form/Input";
import { AsyncAutocompleteOrganisations } from "@components/AsyncAutocomplete/AsyncAutocompleteOrganisations";
import { XMarkIcon } from "@heroicons/react/24/solid";
import Image from "next/image";

interface Props {}

export const HiringOrganisationSection: FC<Props> = () => {
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();

  const hasHiringOrganisation = watch("jobPost.hasHiringOrganisation");
  const jobPostHiringOrganisation = watch("jobPost.hiringOrganisation");

  return (
    <JobPostEditorSectionLayout
      title="Company"
      description="Information about the hiring company."
      hasToggle
      isToggled={hasHiringOrganisation}
      onToggle={() =>
        setValue("jobPost.hasHiringOrganisation", !hasHiringOrganisation)
      }
    >
      {jobPostHiringOrganisation ? (
        <div className="relative p-4 bg-blue-50 border border-blue-200 rounded-md">
          <button
            className="absolute top-4 right-4 px-1 py-1 text-xs bg-transparent rounded"
            onClick={() => setValue("jobPost.hiringOrganisation", null)}
          >
            <XMarkIcon className="w-4 h-4 text-black" />
          </button>
          {jobPostHiringOrganisation.id ? (
            <div className="flex flex-row">
              {jobPostHiringOrganisation.favicon && (
                <div className="mr-4">
                  <Image
                    className="rounded"
                    width={48}
                    height={48}
                    src={`${process.env.NEXT_PUBLIC_SUPABASE_PROJECT_URL}/storage/v1/object/public/public${jobPostHiringOrganisation?.favicon}`}
                    alt={`${jobPostHiringOrganisation?.name || ""} logo`}
                  />
                </div>
              )}
              <div className="flex flex-col">
                <div className="font-bold">
                  {jobPostHiringOrganisation.name}{" "}
                </div>
                <div className="text-sm">
                  {jobPostHiringOrganisation.webUrl}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <div className="col-span-6">
                <Input
                  label="Name"
                  hasOptionalBadge
                  path="jobPost.hiringOrganisation.name"
                  placeholder="Hiring company name"
                  register={register}
                  errors={errors}
                />
              </div>

              <div className="col-span-6 mt-6">
                <Input
                  label="About (short)"
                  hasOptionalBadge
                  path="jobPost.hiringOrganisation.about"
                  placeholder="Your company mission in less than 200 characters."
                  register={register}
                  errors={errors}
                />
              </div>

              <div className="col-span-6 mt-6">
                <Input
                  label="Website"
                  hasOptionalBadge
                  path="jobPost.hiringOrganisation.webUrl"
                  register={register}
                  errors={errors}
                />
              </div>

              <div className="col-span-6 mt-3">
                <Input
                  label="Twitter"
                  hasOptionalBadge
                  path="jobPost.hiringOrganisation.twitterUrl"
                  register={register}
                  registerOptions={{ prefix: "hello" }}
                  errors={errors}
                />
              </div>

              <div className="col-span-6 mt-3">
                <Input
                  label="LinkedIn"
                  hasOptionalBadge
                  path="jobPost.hiringOrganisation.linkedinUrl"
                  register={register}
                  errors={errors}
                />
              </div>

              <div className="col-span-6 mt-3">
                <Input
                  label="GitHub"
                  hasOptionalBadge
                  path="jobPost.hiringOrganisation.githubUrl"
                  register={register}
                  errors={errors}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="">
          {/* <h3 className="mb-2 font-semibold">Select company</h3> */}
          <AsyncAutocompleteOrganisations
            id="hiringOrganisation"
            value={jobPostHiringOrganisation?.id || null}
            setValue={(organisation) => {
              setValue("jobPost.hiringOrganisation", organisation);
            }}
          />
        </div>
      )}
    </JobPostEditorSectionLayout>
  );
};
